/**
 * `/insights` — the dashboard over the brewer's own batches (PLAN-INSIGHTS IN2).
 *
 * **No route guard**, and the absence is still the design: the data is the
 * brewer's own, the maths runs in their browser, and a local-only workspace is a
 * supported way to use the product — a redirect away from arithmetic over your
 * own IndexedDB would cut against both of those. The page is reachable, and it
 * always draws something.
 *
 * What the repackage added is not a guard but a **plan prop**. Insights is a
 * Beerwright feature, so the entitlement is read here — beside the batches and
 * the units, in the one file that touches hooks — and handed down for the page
 * to decide with. A guest's entitlement is the free tier, which is the right
 * answer: they get the preview, on their real batches, with no account needed to
 * see it.
 *
 * The upgrade dialog is the same one every other prompt raises, and it is raised
 * from here for the same reason the plan is read here: the page below takes
 * callbacks, never contexts.
 *
 * This file is the entire wiring: the store's batches through the engine, once,
 * memoised on the list. Everything below it takes props.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useSession } from '@/auth/session-context';
import { InsightsPage } from '@/components/insights/insights-page';
import { usePlanLimit } from '@/components/plan-limit-dialog';
import type { TelemetryAggregateResponse } from '@/fermentation/protocol';
import { telemetryInsightsPath } from '@/fermentation/protocol';
import { costOverTime } from '@/lib/batch-cost';
import { batchInsights, planAllowsInsights } from '@/lib/insights';
import type { TelemetryInsights } from '@/lib/insights-telemetry';
import { telemetryInsights } from '@/lib/insights-telemetry';
import { unitLabel } from '@/lib/units';
import {
  useBatchList,
  useBlendList,
  useJournalList,
  useSettings,
  useUnits,
} from '@/store/workspace-context';

export const Route = createFileRoute('/insights')({
  component: InsightsRoute,
});

/** What {@link useTelemetryAggregate} knows: the answer, and whether it is still coming. */
interface TelemetryAggregate {
  /** The raw wire, or `null` for "no answer" — not yet, or not ever. */
  readonly wire: TelemetryAggregateResponse | null;
  /**
   * True from the moment an enabled request starts until it settles, either
   * way; always false when the plan never asks. This is the one thing `wire`
   * cannot say on its own, because its `null` means both "not answered yet"
   * and "failed" — and only the first of those should hold the panels' space.
   */
  readonly pending: boolean;
}

/**
 * The one fetch on this page (IN3), as a tiny hook so the route below stays a
 * wiring diagram. Asks only on a plan that can be answered — every other plan
 * gets `403 plan_required` and the sample teaser never needed a request — and
 * resolves to the raw wire, `null` for "no answer": network trouble and a
 * refusal both mean the panels stay absent, which is the page's documented
 * posture for data it does not have.
 *
 * `pending` is the part PLAN-LOADING C1 added (D-LD8): while it is true the
 * page holds the two panels' slots, so they do not push the page down when
 * they land. It starts true on an enabled first render, so the first paint
 * already has the blocks rather than nothing followed by blocks. A failure
 * still says nothing and retries nothing; it only stops holding the space.
 */
function useTelemetryAggregate(enabled: boolean): TelemetryAggregate {
  const [wire, setWire] = useState<TelemetryAggregateResponse | null>(null);
  const [pending, setPending] = useState(enabled);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setPending(true);
    void (async () => {
      try {
        const response = await fetch(telemetryInsightsPath());
        if (!response.ok) return;
        const body = (await response.json()) as TelemetryAggregateResponse;
        if (!cancelled && body.ok) setWire(body);
      } catch {
        // Offline is an ordinary state here; the panels simply do not render.
      } finally {
        if (!cancelled) setPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return { wire, pending: enabled && pending };
}

function InsightsRoute() {
  const navigate = useNavigate();
  const batches = useBatchList();
  // Not charted, and read only by the empty state, which says the cellar is not
  // empty even when the charts are (PLAN-UX-PASS E1).
  const blends = useBlendList();
  const journals = useJournalList();
  const units = useUnits();
  const settings = useSettings();
  const session = useSession();
  const { show } = usePlanLimit();
  // `useBatchList` returns a stable array while nothing touched a batch, so this
  // maps `computeBatchActuals` over the log once per change rather than once per
  // render — which matters, because that is the page's whole cost.
  const data = useMemo(() => batchInsights(batches), [batches]);
  const cost = useMemo(
    () => costOverTime(batches, settings.currency, units),
    [batches, settings.currency, units],
  );

  const { wire, pending: telemetryPending } = useTelemetryAggregate(
    planAllowsInsights(session.entitlement.plan),
  );
  const telemetry = useMemo<TelemetryInsights | null>(
    () => (wire === null ? null : telemetryInsights(wire.batches, batches)),
    [wire, batches],
  );

  return (
    <InsightsPage
      data={data}
      units={units}
      plan={session.entitlement.plan}
      onOpenBatches={() => {
        void navigate({ to: '/batches' });
      }}
      onUpgrade={() => show({ reason: 'insights' })}
      telemetry={telemetry}
      telemetryPending={telemetryPending}
      cost={cost}
      currency={settings.currency}
      volumeUnit={unitLabel('volume', units)}
      blendCount={blends.length}
      journalCount={journals.length}
    />
  );
}
